import { Chip, Table, TableBody, TableCell, TableColumn, TableHeader, TableRow } from '@heroui/react'
import { History } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import DisplayTaggedText from './DisplayTaggedText'
import DownloadHistoryButton from './DownloadHistoryButton'
import PlayHistoryButton from './PlayHistoryButton'
import { supportedEffects, useStudioStore } from './utils'

// Empty array constant to avoid recreating on each render
const EMPTY_HISTORY = []

export default function GenerationHistory({ selectedClone }) {
  const [activePlayingId, setActivePlayingId] = useState(null)

  // Extract only what we need from selectedClone to avoid unstable references
  const cloneId = selectedClone?.id
  const cloneName = selectedClone?.name

  // Reset active playing when clone changes
  useEffect(() => {
    setActivePlayingId(null)
  }, [cloneId])

  // Use a stable selector that doesn't create new references
  const generationHistories = useStudioStore(state => state.generationHistories)
  const genHistory = useMemo(
    () => generationHistories[cloneId] || EMPTY_HISTORY,
    [generationHistories, cloneId],
  )

  const formatTimestamp = useCallback((timestamp) => {
    return new Date(timestamp).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }, [])

  const columns = [
    { name: '', key: 'play', width: 60 },
    { name: 'Script', key: 'text' },
    { name: 'Expressiveness', key: 'expressiveness', width: 150 },
    { name: 'Consistency', key: 'consistency', width: 150 },
    { name: 'Time', key: 'timestamp', width: 120 },
    { name: '', key: 'download', width: 60 },
  ]

  const renderCell = useCallback((generation, columnKey) => {
    switch (columnKey) {
      case 'play':
        return (
          <PlayHistoryButton
            generation={generation}
            isActive={activePlayingId === generation.id}
            onPlayStateChange={setActivePlayingId}
          />
        )
      case 'text':
        return (
          <DisplayTaggedText
            text={generation.text}
            supportedEffects={supportedEffects}
            className="text-xs font-mono text-gray-500 truncate line-clamp-2 w-full text-wrap"
          />
        )
      case 'expressiveness':
        return (
          <Chip size="sm" variant="flat" color="primary">
            {(generation.expressiveness * 100).toFixed(0)}
            %
          </Chip>
        )
      case 'consistency':
        return (
          <Chip size="sm" variant="flat" color="secondary">
            {(generation.consistency * 100).toFixed(0)}
            %
          </Chip>
        )
      case 'timestamp':
        return (
          <span className="text-xs text-gray-500">
            {formatTimestamp(generation.timestamp)}
          </span>
        )
      case 'download':
        return (
          <DownloadHistoryButton
            generation={generation}
            voiceName={cloneName}
          />
        )
      default:
        return null
    }
  }, [activePlayingId, formatTimestamp, cloneName])

  return (
    <div className="w-full">
      <label className="font-medium text-sm">
        Recent Generations
      </label>
      {!genHistory?.length
        ? (
            <div className="flex flex-col items-center justify-center h-full pt-10">
              <History className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-500">No recent tests</p>
              <p className="text-xs text-gray-400 mt-1">
                Test your voice clone with a script to see your history here
              </p>
            </div>
          )
        : (
            <Table
              aria-label="Generation history table"
              className="mt-2"
              shadow="none"
              radius="none"
              color="primary"
              isStriped
              classNames={{
                wrapper: '!p-0',
                td: 'border-b border-divider',
                th: 'border-b border-divider font-mono uppercase',
              }}
            >
              <TableHeader columns={columns}>
                {column => (
                  <TableColumn
                    key={column.key}
                    width={column.width}
                    align={column.key === 'play' || column.key === 'download' ? 'center' : 'start'}
                  >
                    {column.name}
                  </TableColumn>
                )}
              </TableHeader>
              <TableBody items={genHistory}>
                {generation => (
                  <TableRow key={generation.id}>
                    {columnKey => (
                      <TableCell>
                        {renderCell(generation, columnKey)}
                      </TableCell>
                    )}
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
    </div>
  )
}
